import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import { useFreefieldState } from "components/Freefields/hooks";
import { GoogleMapLocation } from "components/GoogleMap/GoogleMapLocation";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";
import React, { useEffect, useState } from "react";
import { mapShowToShowInfo } from "show/edit/utils";
import { freeFieldEditableSections, getFreefieldId, isFreefieldKey } from "show/components/freeFields/freeFieldEditableSections";
import { artistSection } from "show/detail/sections/artistSection";
import { hotelSection } from "show/detail/sections/hotelSection";
import { mainSection } from "show/detail/sections/mainSection";
import { promoterSection } from "show/detail/sections/promoterSection";
import { ticketsSection } from "show/detail/sections/ticketsSection";
import { venueSection } from "show/detail/sections/venueSection";
import { ShowInfo } from "show/detail/utils";
import { invalidateShowsCache } from "utilities/api/showQuery";
import { useQueryClient } from "@tanstack/react-query";

interface ShowSidePanelDetailsProps {
  data: server.dto.ShowSidePanelData
  canEdit: boolean;
}

export const ShowSidePanelDetails = (props: ShowSidePanelDetailsProps) => {
  const isInternalUser = site.currentEnvironment.type === "internal";
  const isExternal = !isInternalUser;

  const [show, setShow] = useState<server.dto.Show>(props.data.show);
  const [showInfo, setShowInfo] = useState<ShowInfo>(mapShowToShowInfo(props.data.show));
  const [tags, setTags] = useState<server.dto.TagSmall[]>(props.data.show.tags || []);

  const ffState = useFreefieldState(props.data.show.freeFields, props.data.freeFieldCategories, props.data.freeFieldDefinitionPermissions);

  useEffect(() => {
    setShow(props.data.show);
    setShowInfo(mapShowToShowInfo(props.data.show));
    setTags(props.data.show.tags || []);
  }, [props.data.show]);

  const queryClient = useQueryClient();

  const onValueChanged = async (key: string, value: any) => {

    if (isFreefieldKey(key)) {
      ffState.setEditableFreefieldValue(getFreefieldId(key), value);
      return;
    } else if (key === "tags") {
      setTags(value);
    } else if (key === "artist") {
      setShowInfo(prev => ({ ...prev, artist: value, attendees: [] }));
    } else if (key === "venue") {
      setShowInfo(prev => ({ ...prev, venue: value, venueContact: null, capacity: null, doorsClose: null, doorsOpen: null, ageRestriction: null }));
    } else if (key === "promoter") {
      setShowInfo(prev => ({ ...prev, promoter: value, promoterContact: null }));
    } else if (key === "hotel") {
      setShowInfo(prev => ({ ...prev, hotel: value, hotelContact: null }));
    } else if (key === "ageRestriction") {
      setShowInfo(prev => ({ ...prev, ageRestriction: value?.value ?? value }));
    } else if (key === "currencyAndExchangeRate") {
      setShowInfo(prev => ({ ...prev, currency: value.currency, exchangeRate: value.exchangeRate }));
    } else {
      setShowInfo(prev => ({ ...prev, [key]: value }));
    }
  };

  const onSuccessfulUpdate = async (key: string) => {
    if (isFreefieldKey(key)) {
      return;
    }

    const res = await window.CoreApi.Show.GetShow(show.showId, { toastOnError: true });
    if (!res.data.hasErrors) {
      const newShow = res.data.data;
      setShow(newShow);
      setShowInfo(mapShowToShowInfo(newShow));
      setTags(newShow.tags || []);
    }
    invalidateShowsCache(queryClient);
  };

  const canInPageEdit = props.canEdit && show.permissions.canModify;

  return <SidePanelDetailSectionTemplate
    isInPageEditable={canInPageEdit}
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    fields={[

      mainSection(show, showInfo, tags, isExternal, true),
      artistSection(show, showInfo, isExternal, true),
      venueSection(show, showInfo, isExternal, true),
      promoterSection(show, showInfo, isExternal, true),
      hotelSection(show, showInfo, isExternal, true),
      ticketsSection(show, showInfo, isExternal),
      ...freeFieldEditableSections(
        show.showId,
        ffState.freefieldCategoriesWithValuesAndPermissions,
        window.CoreApi.Show.UpdateFreeField,
      ),
    ]}
    additionalContent={<>
      {show.venue?.location && <>
        <ViewOnMap height={400}>
          <GoogleMapLocation
            pin={{
              type: "show",
              phase: "unknown"
            }}
            location={show.venue.location}
          />
        </ViewOnMap>
      </>}
    </>}
  />
}
